import React, { useEffect, createElement } from 'react';
import { SocialProof } from './components/SocialProof';
import { PainPoints } from './components/PainPoints';
import { ProductShowcase } from './components/ProductShowcase';
import { WhatYouGet } from './components/WhatYouGet';
import { Bonuses } from './components/Bonuses';
import { Countdown } from './components/Countdown';
import { Pricing } from './components/Pricing';
import { ProBusiness } from './components/ProBusiness';
import { Guarantee } from './components/Guarantee';
import { Testimonials } from './components/Testimonials';
import { FAQ } from './components/FAQ';
import { DarkCTA } from './components/DarkCTA';
import { Footer } from './components/Footer';
import { PurchaseNotifications } from './components/PurchaseNotifications';
import { initFbPixel, trackFbEvent, trackSectionViews, trackScrollDepth, trackTimeOnPage } from './utils/fbPixel';
const HERO_IMG = 'https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,w_760/v1777938271/heylloyd-image-1777865023115_1_-Photoroom_mrxxw9.webp';
const HERO_IMG_SRCSET = 'https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,w_400/v1777938271/heylloyd-image-1777865023115_1_-Photoroom_mrxxw9.webp 400w, https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,w_600/v1777938271/heylloyd-image-1777865023115_1_-Photoroom_mrxxw9.webp 600w, https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,w_760/v1777938271/heylloyd-image-1777865023115_1_-Photoroom_mrxxw9.webp 760w';
const OG_IMG = 'https://cdn.magicpatterns.com/uploads/9uMhQHg3RXXbKPqANDF9n5/hf_20260423_164447_343907b5-95aa-49ff-ba71-eeea9d0be6f6-Photoroom.webp';
const TAGS = ['📄 100% PDF', '⚡ Acceso Inmediato', '🇦🇷 Argentina', '🎓 Toda la Carrera', '✅ Sistema Listo', '📚 6 Tomos + 6 Bonos'];
export function App() {
  useEffect(() => {
    const runSeo = () => {
      document.title = 'MegaPack Psicología: Carrera Resuelta — El Sistema Completo para Estudiantes de Psicología en Argentina';
      document.documentElement.lang = 'es';
      const setMeta = (name: string, content: string, property?: boolean) => {
        const attr = property ? 'property' : 'name';
        let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.content = content;
      };
      const description = 'El sistema completo de estudio para estudiantes de Psicología en Argentina. 6 Tomos con toda la carrera organizada por etapas, más 6 bonos exclusivos creados por psicólogos y profesionales. Organizá tu cursado, ahorrá cientos de horas y llegá a cada examen con un sistema claro. Acceso inmediato en formato PDF.';
      const url = 'https://www.bibliotecaprofesional.com';
      const image = OG_IMG;
      setMeta('description', description);
      setMeta('keywords', 'megapack psicología, carrera resuelta, estudiar psicología argentina, organizar carrera psicología, sistema estudio psicología argentina, material psicología universitario, apuntes psicología UBA UNLP UNC, biblioteca profesional, aprobar psicología argentina, orden carrera psicología');
      setMeta('robots', 'index, follow');
      setMeta('author', 'Biblioteca Profesional');
      setMeta('viewport', 'width=device-width, initial-scale=1');
      if (!document.querySelector('link[rel="canonical"]')) {
        const canonical = document.createElement('link');
        canonical.rel = 'canonical';
        canonical.href = url;
        document.head.appendChild(canonical);
      }
      setMeta('og:title', 'MegaPack Psicología: Carrera Resuelta — El Sistema Completo para Estudiantes de Psicología en Argentina', true);
      setMeta('og:description', description, true);
      setMeta('og:image', image, true);
      setMeta('og:url', url, true);
      setMeta('og:type', 'product', true);
      setMeta('og:locale', 'es_AR', true);
      setMeta('og:site_name', 'Biblioteca Profesional™', true);
      setMeta('twitter:card', 'summary_large_image');
      setMeta('twitter:title', 'MegaPack Psicología: Carrera Resuelta — El Sistema Completo para Estudiantes de Psicología');
      setMeta('twitter:description', description);
      setMeta('twitter:image', image);
      if (!document.querySelector('script[data-ld="product"]')) {
        const ld = document.createElement('script');
        ld.type = 'application/ld+json';
        ld.setAttribute('data-ld', 'product');
        ld.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'MegaPack Psicología: Carrera Resuelta™',
          description,
          image,
          brand: {
            '@type': 'Brand',
            name: 'Biblioteca Profesional™'
          },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '19990',
            highPrice: '27990',
            priceCurrency: 'ARS',
            availability: 'https://schema.org/InStock',
            offerCount: 2
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '487'
          }
        });
        document.head.appendChild(ld);
      }
      if (!document.querySelector('script[data-ld="org"]')) {
        const orgLd = document.createElement('script');
        orgLd.type = 'application/ld+json';
        orgLd.setAttribute('data-ld', 'org');
        orgLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Biblioteca Profesional™',
          url,
          logo: image,
          email: 'hola@bibliotecaprofesional.com',
          sameAs: ['https://www.instagram.com/bibliotecaprofesional']
        });
        document.head.appendChild(orgLd);
      }
      if (!document.querySelector('script[data-ld="faq"]')) {
        const faqLd = document.createElement('script');
        faqLd.type = 'application/ld+json';
        faqLd.setAttribute('data-ld', 'faq');
        faqLd.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{
            '@type': 'Question',
            name: '¿Qué incluye el MegaPack Psicología: Carrera Resuelta?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '6 Tomos de aproximadamente 60 páginas cada uno con toda la carrera de Psicología organizada por etapa y área, más 6 bonos exclusivos creados por psicólogos y profesionales. Todo en formato PDF de acceso inmediato.'
            }
          }, {
            '@type': 'Question',
            name: '¿Cómo recibo el material?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Todo es 100% digital en formato PDF. Recibís acceso inmediato en tu email dentro de los 5 minutos posteriores a la compra.'
            }
          }, {
            '@type': 'Question',
            name: '¿Está adaptado a Argentina?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Sí, 100%. Todo el contenido está adaptado al plan de estudios argentino, con los autores, textos y perspectivas teóricas utilizadas en las universidades nacionales como UBA, UNLP y UNC.'
            }
          }, {
            '@type': 'Question',
            name: '¿Para qué año de la carrera está pensado?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Para toda la carrera, con especial foco en 1° a 3° año. Cada tomo cubre una etapa específica del recorrido académico, desde los fundamentos hasta la clínica y el contexto social.'
            }
          }, {
            '@type': 'Question',
            name: '¿Quién creó el MegaPack Psicología?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'El MegaPack Psicología fue creado y revisado por psicólogos y profesionales especializados de Argentina, con el objetivo de darle a los estudiantes el sistema de organización y estudio que la facultad nunca les enseñó.'
            }
          }]
        });
        document.head.appendChild(faqLd);
      }
    };
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(runSeo, {
        timeout: 1500
      });
    } else {
      setTimeout(runSeo, 100);
    }
  }, []);
  useEffect(() => {
    const start = () => initFbPixel();
    if ('requestIdleCallback' in window) {
      ;
      (window as any).requestIdleCallback(start, {
        timeout: 2500
      });
    } else {
      setTimeout(start, 1500);
    }
  }, []);
  useEffect(() => {
    return trackSectionViews({
      '[data-section="hero"]': 'Hero',
      '[data-section="social-proof"]': 'Social Proof',
      '[data-section="pain-points"]': 'Pain Points',
      '[data-section="product-showcase"]': 'Product Showcase',
      '[data-section="what-you-get"]': 'What You Get',
      '[data-section="bonuses"]': 'Bonuses',
      '[data-section="countdown"]': 'Countdown / Urgency',
      '#pricing': 'Pricing',
      '[data-section="pro-business"]': 'Máster Psicología',
      '[data-section="guarantee"]': 'Guarantee',
      '[data-section="testimonials"]': 'Testimonials',
      '[data-section="faq"]': 'FAQ',
      '[data-section="dark-cta"]': 'Final CTA',
      '[data-section="footer"]': 'Footer'
    });
  }, []);
  useEffect(() => {
    return trackScrollDepth();
  }, []);
  useEffect(() => {
    return trackTimeOnPage();
  }, []);
  return <>
      <noscript data-id="element-0">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=1215304437119773&ev=PageView&noscript=1" alt="" data-id="element-1" />
      </noscript>

      <header data-section="hero" className="relative flex w-full flex-col items-center justify-center text-center text-gray-900 text-base leading-6 px-5 sm:px-6 py-12 sm:py-16" role="banner" style={{
      backgroundImage: 'linear-gradient(180deg, #FEFAF4 0%, #FDF4E7 50%, #FEFAF4 100%)',
      backgroundColor: '#FEFAF4'
    }} data-id="element-2">
        {/* Badge */}
        <div className="mb-5 inline-flex items-center gap-2 rounded-full px-4 py-1.5 shadow-sm" style={{
        border: '1px solid rgba(122,158,126,0.35)',
        backgroundColor: 'rgba(255,255,255,0.88)'
      }} data-id="element-3">
          <span className="text-xs sm:text-sm font-bold tracking-wide uppercase" style={{
          color: '#7A9E7E'
        }} data-id="element-4">
            🎓 Sistema de Estudio para Psicología · Estudiantes Argentina
          </span>
        </div>

        {/* H1 */}
        <h1 className="sm:text-5xl md:text-[72px] sm:leading-snug md:leading-[88px] font-black max-w-4xl m-0 mb-2 tracking-tight pt-[10px] pb-[10px] text-[23px]" style={{
        color: '#1E1208'
      }} data-id="element-5">
          Toda la carrera de Psicología
          <br data-id="element-6" />
          en un solo sistema.
          <br data-id="element-7" />
          <span className="mt-1 inline-block" style={{
          color: '#C8922A'
        }} data-id="element-8">
            Sin buscar más, sin improvisar.
          </span>
        </h1>

        {/* Mockup (LCP element) */}
        <div className="my-6 sm:my-8 w-full max-w-[320px] sm:max-w-[380px] aspect-square relative" data-id="element-9">
          <div className="absolute inset-4 rounded-full z-0 pointer-events-none" style={{
          background: 'radial-gradient(circle, rgba(200,146,42,0.22) 0%, rgba(122,158,126,0.10) 55%, transparent 75%)'
        }} data-id="element-10" />
          <div className="relative z-10 w-full h-full" data-id="element-11">
            <img src={HERO_IMG} srcSet={HERO_IMG_SRCSET} sizes="(min-width: 640px) 380px, 320px" alt="MegaPack Psicología: Carrera Resuelta — Sistema Completo para Estudiantes de Psicología Argentina" width="760" height="760" fetchPriority="high" decoding="sync" className="block max-w-full w-full h-auto object-contain" data-id="element-12" />
          </div>
        </div>

        {/* Subtítulo */}
        <p className="max-w-xl text-base sm:text-lg font-semibold leading-7 m-0 mb-4" style={{
        color: '#6B4C35'
      }} data-id="element-13">
          El sistema que los mejores estudiantes de Psicología usan para
          organizar toda la carrera, llegar a cada parcial con claridad y
          terminar la cursada{' '}
          <span className="font-black" style={{
          color: '#1E1208'
        }} data-id="element-14">
            sin perder horas armando resúmenes que después no sirven.
          </span>
        </p>

        {/* Tagline */}
        <p className="max-w-xl text-lg sm:text-xl font-black m-0 mb-6" style={{
        color: '#C8922A'
      }} data-id="element-15">
          "Más de 360 páginas organizadas por etapa, listas para usar. Del
          primer día de cursada al último final."
        </p>

        {/* Tags */}
        <div className="mb-7 flex max-w-lg flex-wrap items-center justify-center gap-2" data-id="element-16">
          {TAGS.map(tag => <span key={tag} className="whitespace-nowrap text-xs sm:text-sm font-semibold leading-5 rounded-full px-3 py-1 shadow-sm" style={{
          backgroundColor: 'rgba(255,255,255,0.88)',
          color: '#1E1208',
          border: '1px solid rgba(200,146,42,0.28)'
        }} data-id="element-17">
              {tag}
            </span>)}
        </div>

        {/* CTA Button */}
        <a href="#pricing" onClick={() => {
        trackFbEvent('Lead', {
          content_name: 'Hero CTA Click',
          content_category: 'Psicología Carrera Resuelta'
        });
      }} className="block w-full max-w-sm text-white text-lg sm:text-xl font-black rounded-xl px-6 sm:px-8 py-4 mb-4 no-underline text-center shadow-lg hover:opacity-90 transition-opacity" style={{
        background: 'linear-gradient(135deg, #B8860B 0%, #C8922A 50%, #7A9E7E 100%)'
      }} data-id="element-18">
          🎓 → QUIERO MI MEGAPACK AHORA
        </a>

        {/* Trust Badges */}
        <div className="flex flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 m-0" data-id="element-19">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm shadow-sm" style={{
          backgroundColor: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(200,146,42,0.22)',
          color: '#6B4C35'
        }} data-id="element-20">
            🛡️ Garantía 7 días
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm shadow-sm" style={{
          backgroundColor: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(200,146,42,0.22)',
          color: '#6B4C35'
        }} data-id="element-21">
            🔒 Pago seguro
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs sm:text-sm shadow-sm" style={{
          backgroundColor: 'rgba(255,255,255,0.88)',
          border: '1px solid rgba(200,146,42,0.22)',
          color: '#6B4C35'
        }} data-id="element-22">
            💳 Mercado Pago | Tarjeta | Transferencia
          </span>
        </div>

        {/* Degradé inferior → conecta con SocialProof */}
        <div className="absolute bottom-0 left-0 w-full h-16 pointer-events-none" style={{
        background: 'linear-gradient(180deg, transparent 0%, #FDF4E7 100%)'
      }} data-id="element-23" />
      </header>

      <main data-id="element-24">
        <div data-section="social-proof" data-id="element-25">
          <SocialProof data-id="element-26" />
        </div>
        <div data-section="pain-points" data-id="element-27">
          <PainPoints data-id="element-28" />
        </div>
        <div data-section="product-showcase" data-id="element-29">
          <ProductShowcase data-id="element-30" />
        </div>
        <div data-section="what-you-get" data-id="element-31">
          <WhatYouGet data-id="element-32" />
        </div>
        <div data-section="bonuses" data-id="element-33">
          <Bonuses data-id="element-34" />
        </div>
        <div data-section="countdown" data-id="element-35">
          <Countdown data-id="element-36" />
        </div>
        <Pricing data-id="element-37" />
        <div data-section="pro-business" data-id="element-38">
          <ProBusiness data-id="element-39" />
        </div>
        <div data-section="guarantee" data-id="element-40">
          <Guarantee data-id="element-41" />
        </div>
        <div data-section="testimonials" data-id="element-42">
          <Testimonials data-id="element-43" />
        </div>
        <div data-section="faq" data-id="element-44">
          <FAQ data-id="element-45" />
        </div>
        <div data-section="dark-cta" data-id="element-46">
          <DarkCTA data-id="element-47" />
        </div>
      </main>

      <div data-section="footer" data-id="element-48">
        <Footer data-id="element-49" />
      </div>
      <PurchaseNotifications data-id="element-50" />
    </>;
}